import React, { useEffect, useMemo, useState } from "react";
import config from "../../config.js";
import PdfButton from "../ui/PdfButton.jsx";
import ErrorBoundary from "../../components/ErrorBoundary.jsx";
import TmInvoiceLockNote from "../../components/tm/TmInvoiceParts.jsx";
import {
  chainAlignmentMeta,
  groupInvoiceLines,
  inquiryBalanceAsked,
  inquiryForInvoice,
  invoiceBalance,
  invoiceStatusDisplay,
  isCustomerPoDoc,
  loadInvoice,
  loadInvoiceEmails,
  loadPaymentInquiries,
} from "../../features/invoices/invoiceData";
import { invoicePdfPath } from "../../features/salesOrders/soData";
import { fmt } from "../../utils/financeFormat";
import { shopToday } from "../../utils/shopDate";
import { fmtDay } from "./salesOrders/format";
import "../mobile.css";

// One invoice on a phone, READ-ONLY (plan Phase 2): status (Bill.com), money (paid, balance, past
// due, retainage), the quote → SO → invoice check, the order's customer documents, lines (assemblies
// as one row), payments, the PDF, the emails sent and the payment inquiries. No write control of any
// kind. Layout only: features/invoices/invoiceData.js is the desktop page's own code.

const API_BASE = config.API_BASE_URL;
const CHIP = { success: "jq-status jq-status-done", info: "jq-status jq-status-progress", warning: "jq-tag jq-tag-warn", error: "jq-status jq-status-cancelled", default: "jq-tag" };
const NOTE = { success: "jq-note-info", info: "jq-note-info", warning: "jq-note-warn", error: "jq-note-danger" };

function Chain({ ca, invoiceNumber, status }) {
  const meta = chainAlignmentMeta(ca.status);
  return (
    <section className={`jq-note ${NOTE[meta.severity] || "jq-note-info"} mb-2`} data-testid="inv-chain">
      <div className="fw-semibold">{meta.title}</div>
      {ca.quote && <div>Quote #{ca.quote.quote_id} · {ca.quote.status} · {fmt(ca.quote.computed_total)} · {ca.quote.line_count} line{ca.quote.line_count === 1 ? "" : "s"}</div>}
      {ca.sales_order && (
        <div>
          {ca.sales_order.order_number} · {ca.sales_order.status} · {fmt(ca.sales_order.computed_total)}; billed {fmt(ca.sales_order.cumulative_invoiced)} across {ca.sales_order.invoice_count} invoice{ca.sales_order.invoice_count === 1 ? "" : "s"}
        </div>
      )}
      <div>#{invoiceNumber} · {status} · {fmt(ca.invoice_total)}{ca.sales_order ? `; ${fmt(ca.sales_order.remaining_to_bill)} left to bill on the order` : ""}</div>
      {(ca.flags || []).map((f, i) => <div key={i} className="text-danger">⚠ {f}</div>)}
    </section>
  );
}

export default function InvoiceDetailScreen({ invoiceId, onAll }) {
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [emails, setEmails] = useState(null);
  const [inquiries, setInquiries] = useState(null);
  const [openInquiry, setOpenInquiry] = useState(null);

  useEffect(() => {
    let live = true;
    loadInvoice(invoiceId)
      .then((d) => { if (live) setInv(d); })
      .catch((err) => { if (live) setError(err?.response?.data?.detail || err.message || "Couldn't reach JobQ."); });
    loadInvoiceEmails(invoiceId).then((d) => { if (live) setEmails(d); }).catch(() => { if (live) setEmails([]); });
    loadPaymentInquiries(invoiceId).then((d) => { if (live) setInquiries(d); }).catch(() => { if (live) setInquiries([]); });
    return () => { live = false; };
  }, [invoiceId]);

  const lines = useMemo(() => groupInvoiceLines(inv), [inv]);

  const back = <button type="button" className="btn btn-link px-0 mb-2" onClick={onAll}><i className="fas fa-chevron-left me-1" aria-hidden="true" />All invoices</button>;
  if (error && !inv) return <div className="jq-screen" data-testid="phone-invoice">{back}<div className="jq-note jq-note-danger" role="alert">{error}</div></div>;
  if (!inv) return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;

  const { display, chipColor } = invoiceStatusDisplay(inv);
  const money = invoiceBalance(inv, shopToday());
  const docs = inv.so_documents || [];
  const payments = inv.payments || [];

  return (
    <div className="jq-screen" data-testid="phone-invoice">
      {back}
      <section className="jq-card" data-testid="inv-header">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold fs-5 flex-grow-1">Invoice #{inv.invoice_number}</span>
          <span className={CHIP[chipColor] || "jq-tag"}>{display}</span>
        </div>
        <div className="fw-semibold">{inv.customer_name}</div>
        <div className="jq-muted">
          {inv.bill_com_invoice_id ? "In Bill.com" : "Not in Bill.com"}
          {inv.last_emailed_at ? ` · emailed ${new Date(inv.last_emailed_at).toLocaleDateString()}` : ""}
          {inquiries && inquiries.length > 0 ? ` · inquiry sent ${new Date(inquiries[0].sent_at).toLocaleDateString()}` : ""}
        </div>
        <dl className="jq-kv mt-2 mb-0" data-testid="inv-money">
          <div><dt>Sales order</dt><dd>{inv.order_number || "N/A"}</dd></div>
          <div><dt>Terms</dt><dd>{inv.payment_terms || "—"}</dd></div>
          <div><dt>Invoiced</dt><dd>{inv.invoice_date ? fmtDay(inv.invoice_date) : "—"}</dd></div>
          <div><dt>Due</dt><dd className={money.pastDue && money.balance > 0.01 ? "text-danger" : ""}>{inv.due_date ? fmtDay(inv.due_date) : "—"}</dd></div>
          <div><dt>Subtotal</dt><dd>{fmt(inv.subtotal || 0)}</dd></div>
          {Number(inv.shipping_amount) > 0 && <div><dt>Shipping</dt><dd>{fmt(inv.shipping_amount)}</dd></div>}
          {Number(inv.additional_fees) > 0 && <div><dt>Fees</dt><dd>{fmt(inv.additional_fees)}</dd></div>}
          {Number(inv.discount_amount) > 0 && <div><dt>Discount</dt><dd>{fmt(inv.discount_amount)}</dd></div>}
          {Number(inv.tax_amount) > 0 && <div><dt>Tax</dt><dd>{fmt(inv.tax_amount)}</dd></div>}
          <div><dt>Total</dt><dd>{fmt(inv.total || 0)}</dd></div>
          {money.paid > 0.01 && <div><dt>Paid</dt><dd className="text-success">{fmt(money.paid)}</dd></div>}
          {money.balance > 0.01 && (
            <div>
              <dt>{money.balanceIsAllRetainage ? "Balance (retainage)" : "Balance"}</dt>
              <dd className={money.balanceIsAllRetainage ? "" : money.pastDue ? "text-danger" : ""} data-testid="inv-balance">{fmt(money.balance)}</dd>
            </div>
          )}
        </dl>
        {money.retainageOpen && (
          <div className="jq-note jq-note-warn mt-2" data-testid="inv-retainage">
            {fmt(money.retainage)} held as retainage{inv.retainage_held_at ? ` since ${new Date(inv.retainage_held_at).toLocaleDateString()}` : ""}
          </div>
        )}
        {inv.notes && <div className="jq-muted mt-2" style={{ whiteSpace: "pre-wrap" }}>{inv.notes}</div>}
        <ErrorBoundary label="The T&M invoice note">
          <TmInvoiceLockNote invoice={inv} />
        </ErrorBoundary>
      </section>

      {inv.chain_alignment && <Chain ca={inv.chain_alignment} invoiceNumber={inv.invoice_number} status={inv.status} />}

      <h2 className="jq-section-title mt-3">Lines</h2>
      {lines.map((ln) => (
        <div key={ln.invoice_line_id} className="jq-card" data-testid={`inv-line-${ln.invoice_line_id}`}>
          <div className="fw-semibold">{ln.product_id === "ASSEMBLY" ? ln.description : ln.product_id}</div>
          {ln.product_id !== "ASSEMBLY" && ln.description && <div className="jq-muted">{ln.description}</div>}
          <div>{ln.quantity}{ln.product_id === "ASSEMBLY" ? " set(s)" : ""} × {fmt(ln.unit_price || 0)} = {fmt((ln.quantity || 0) * (ln.unit_price || 0))}</div>
          <div className="jq-muted">
            {ln.master_job_number || ln.job_id ? `Job ${ln.master_job_number || ln.job_id}` : ""}
            {ln.customer_po ? ` · PO ${ln.customer_po}` : ""}
            {ln.customer_so ? ` · cust SO ${ln.customer_so}` : ""}
          </div>
          {ln._per_set_source?.note && <div className="jq-muted">{ln._per_set_source.note}</div>}
        </div>
      ))}

      {payments.length > 0 && (
        <>
          <h2 className="jq-section-title mt-3">Payments</h2>
          {payments.map((p) => (
            <div key={p.invoice_payment_id} className="jq-card" data-testid={`inv-payment-${p.invoice_payment_id}`}>
              <div className="fw-semibold">{fmt(p.payment_amount || 0)} · {p.payment_method || "—"} · {p.payment_date ? fmtDay(p.payment_date) : "—"}</div>
              {p.payment_group_id && <div className="jq-muted">Check #{p.check_number || "—"} ({fmt(p.group_total || 0)} total)</div>}
              {p.memo && <div className="jq-muted">{p.memo}</div>}
              {(p.documents || []).map((d) => (
                <a key={d.payment_document_id} href={d.url} target="_blank" rel="noreferrer" className="d-block">{d.doc_type || "Document"}{d.original_filename ? `: ${d.original_filename}` : ""}</a>
              ))}
            </div>
          ))}
        </>
      )}

      <section className="jq-card mt-3" data-testid="inv-documents">
        <h2 className="jq-section-title">Documents</h2>
        <PdfButton url={`${API_BASE}${invoicePdfPath(inv.invoice_id)}`} filename={`Invoice_${inv.invoice_number}.pdf`} label={`Invoice #${inv.invoice_number} PDF`} testId="inv-pdf" />
        {docs.map((d) => (
          <a key={d.document_id} href={d.s3_url} target="_blank" rel="noreferrer" className="d-block mt-2">
            {d.display_name || d.original_filename}
            {isCustomerPoDoc(d) ? <span className="jq-tag ms-1">Customer PO</span> : <span className="jq-muted"> · {d.doc_type || "Document"}</span>}
          </a>
        ))}
      </section>

      <section className="jq-card" data-testid="inv-emails">
        <h2 className="jq-section-title">Emails</h2>
        {emails === null && <div className="jq-muted">Loading…</div>}
        {emails && emails.length === 0 && <div className="jq-muted">Not emailed from JobQ.</div>}
        {(emails || []).map((e) => (
          <div key={e.snapshot_id} className="jq-task-line">
            <span className="jq-task-line-main">
              {(e.action_type || "").replace(/^Emailed to\s*/i, "")}
              <div className="jq-muted">{new Date(e.created_at).toLocaleString()}</div>
            </span>
            {e.s3_url && <a href={e.s3_url} target="_blank" rel="noreferrer" className="btn btn-link btn-sm">PDF</a>}
          </div>
        ))}
      </section>

      {inquiries && inquiries.length > 0 && (
        <section className="jq-card" data-testid="inv-inquiries">
          <h2 className="jq-section-title">Payment inquiries</h2>
          {inquiries.map((pi) => {
            const mine = inquiryForInvoice(pi, inv.invoice_id);
            const isOpen = openInquiry === pi.inquiry_id;
            return (
              <div key={pi.inquiry_id} className="mb-2" data-testid={`inv-inquiry-${pi.inquiry_id}`}>
                <div className="fw-semibold">{new Date(pi.sent_at).toLocaleString()} · {pi.sent_by || "—"}</div>
                <div className="jq-muted">To {[pi.to_email, ...(pi.cc_emails || [])].join(", ")}</div>
                <div className="jq-muted">{(pi.invoice_numbers || []).map((n) => `#${n}`).join(", ")} · asked about {inquiryBalanceAsked(mine)}</div>
                <button type="button" className="btn btn-link btn-sm px-0" aria-expanded={isOpen} onClick={() => setOpenInquiry(isOpen ? null : pi.inquiry_id)}>{isOpen ? "Hide the message" : "Show the message"}</button>
                {mine.pdf_url && <a href={mine.pdf_url} target="_blank" rel="noreferrer" className="btn btn-link btn-sm">PDF</a>}
                {isOpen && (
                  <div className="border rounded p-2 small">
                    <div className="fw-semibold mb-1">{pi.subject}</div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{pi.body_text}</div>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}
